import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Clock, ArrowRight, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import JsonLd from '../components/JsonLd';
import { BlogIconBanner } from '../components/BlogIconBanner';
import { blogArticles } from '../data/blogArticles';
import { Card, CardContent } from '../components/ui/card';

const BlogPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Blog Marabout Sérieux ⭐ Conseils Retour Affectif, Voyance, Rituels</title>
        <meta name="description" content="Blog du Grand Marabout : articles et guides sur le retour affectif, faire revenir son ex, voyance sérieuse, rituels puissants. Conseils d'expert pour récupérer l'être aimé." />
        <meta name="keywords" content="blog marabout, retour affectif conseils, faire revenir son ex guide, voyance sérieuse blog, rituels marabout, récupérer son ex" />
        <link rel="canonical" href="https://maraboutserieux.com/blog" />
        <link rel="alternate" hrefLang="fr" href="https://maraboutserieux.com/blog" />
      </Helmet>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Blog Marabout Sérieux",
        "url": "https://maraboutserieux.com/blog",
        "description": "Articles et guides sur le retour affectif, faire revenir son ex, voyance sérieuse et rituels puissants.",
        "publisher": {
          "@type": "Organization",
          "name": "Marabout Sérieux",
          "url": "https://maraboutserieux.com"
        },
        "blogPost": blogArticles.map(article => ({
          "@type": "BlogPosting",
          "headline": article.title,
          "url": `https://maraboutserieux.com/blog/${article.slug}`,
          "datePublished": article.publishDate,
          "dateModified": article.modifiedDate,
          "description": article.metaDescription
        }))
      }} />

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://maraboutserieux.com/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://maraboutserieux.com/blog" }
        ]
      }} />

      <Header />

      <section className="pt-28 pb-10 bg-gradient-to-b from-black to-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">
              Blog du Grand Marabout
            </h1>
            <p className="text-base md:text-lg text-amber-100/70 max-w-2xl mx-auto">
              Guides, conseils et témoignages sur le retour affectif, la voyance sérieuse et les rituels puissants du Grand Marabout.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {blogArticles.map((article) => (
              <Link key={article.id} to={`/blog/${article.slug}`} data-testid={`blog-card-${article.slug}`}>
                <Card className="bg-gradient-to-br from-zinc-900 to-black border-amber-600/30 hover:border-amber-500/60 transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-amber-500/20 group h-full">
                  <div className="overflow-hidden rounded-t-lg">
                    <BlogIconBanner icon={article.icon} size="small" />
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center gap-1 text-xs px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-amber-300">
                        <Tag size={12} />
                        {article.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-amber-100/50">
                        <Clock size={12} />
                        {article.readTime}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-amber-400 mb-2 group-hover:text-amber-300 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-amber-100/60 leading-relaxed line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center text-amber-400 text-sm font-semibold group-hover:text-amber-300">
                      Lire l'article
                      <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
